import { useState, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';

const NOTIFICATION_PROMPT_PREFIX = 'therappi_notification_prompt_';

// All feature areas that can trigger notification prompts
export type NotificationFeatureArea = 
  | 'check-in' 
  | 'cycle-tracker' 
  | 'food-scanner' 
  | 'medication' 
  | 'manifestation' 
  | 'sleep' 
  | 'therapy-chat' 
  | 'goals' 
  | 'breathwork'
  | 'meditation'
  | 'journal'
  | 'gratitude'
  | 'habits'
  | 'general';

export type TriggerContext = 'check-in' | 'habit' | 'general';

// Map feature areas to trigger contexts for the modal
const featureToContext: Record<NotificationFeatureArea, TriggerContext> = {
  'check-in': 'check-in',
  'cycle-tracker': 'habit',
  'food-scanner': 'habit',
  'medication': 'habit',
  'manifestation': 'habit',
  'sleep': 'habit',
  'therapy-chat': 'general',
  'goals': 'general',
  'breathwork': 'habit',
  'meditation': 'habit',
  'journal': 'habit',
  'gratitude': 'habit',
  'habits': 'habit',
  'general': 'general'
};

export const useNotificationPrompt = () => {
  const [shouldShowPrompt, setShouldShowPrompt] = useState(false);
  const [triggerContext, setTriggerContext] = useState<TriggerContext>('check-in');
  const [currentFeature, setCurrentFeature] = useState<NotificationFeatureArea>('general');

  // Check if a specific feature's prompt has been shown
  const hasFeatureBeenShown = useCallback((feature: NotificationFeatureArea) => {
    return localStorage.getItem(`${NOTIFICATION_PROMPT_PREFIX}${feature}_shown`) === 'true';
  }, []);

  // Check if a specific feature's prompt has been dismissed
  const hasFeatureBeenDismissed = useCallback((feature: NotificationFeatureArea) => {
    return localStorage.getItem(`${NOTIFICATION_PROMPT_PREFIX}${feature}_dismissed`) === 'true';
  }, []);

  // Check if ANY prompt has been shown (for backwards compatibility)
  const hasBeenShown = useCallback(() => {
    return localStorage.getItem(`${NOTIFICATION_PROMPT_PREFIX}shown`) === 'true';
  }, []);

  // Check if user has globally dismissed prompts
  const hasBeenDismissed = useCallback(() => {
    return localStorage.getItem(`${NOTIFICATION_PROMPT_PREFIX}dismissed`) === 'true';
  }, []);

  // Mark a specific feature's prompt as shown
  const markFeatureAsShown = useCallback((feature: NotificationFeatureArea) => {
    localStorage.setItem(`${NOTIFICATION_PROMPT_PREFIX}${feature}_shown`, 'true');
    localStorage.setItem(`${NOTIFICATION_PROMPT_PREFIX}shown`, 'true');
  }, []);

  // Mark a specific feature's prompt as dismissed
  const markFeatureAsDismissed = useCallback((feature: NotificationFeatureArea) => {
    localStorage.setItem(`${NOTIFICATION_PROMPT_PREFIX}${feature}_dismissed`, 'true');
  }, []);

  // Mark prompt as globally dismissed
  const markAsDismissed = useCallback(() => {
    localStorage.setItem(`${NOTIFICATION_PROMPT_PREFIX}dismissed`, 'true');
  }, []);

  // Trigger the prompt for a specific feature
  // Only shows if: native platform, feature not shown before, not globally dismissed
  const triggerPromptForFeature = useCallback((feature: NotificationFeatureArea) => {
    // Only show on native platforms
    if (!Capacitor.isNativePlatform()) {
      return false;
    }

    // Don't show if globally dismissed
    if (hasBeenDismissed()) {
      return false;
    }

    // Don't show if this specific feature has already been shown or dismissed
    if (hasFeatureBeenShown(feature) || hasFeatureBeenDismissed(feature)) {
      return false;
    }

    setCurrentFeature(feature);
    setTriggerContext(featureToContext[feature]);
    setShouldShowPrompt(true);
    markFeatureAsShown(feature);
    return true;
  }, [hasBeenDismissed, hasFeatureBeenShown, hasFeatureBeenDismissed, markFeatureAsShown]);

  // Legacy trigger for backwards compatibility
  const triggerPrompt = useCallback((context: TriggerContext = 'check-in') => {
    const featureMap: Record<TriggerContext, NotificationFeatureArea> = {
      'check-in': 'check-in',
      'habit': 'habits',
      'general': 'general'
    };
    return triggerPromptForFeature(featureMap[context]);
  }, [triggerPromptForFeature]);

  // Close the prompt
  const closePrompt = useCallback(() => {
    setShouldShowPrompt(false);
  }, []);

  // Dismiss (user said "not now") - marks current feature as dismissed
  const dismissPrompt = useCallback(() => {
    setShouldShowPrompt(false);
    markFeatureAsDismissed(currentFeature);
  }, [currentFeature, markFeatureAsDismissed]);

  // Dismiss globally (never show again)
  const dismissPromptGlobally = useCallback(() => {
    setShouldShowPrompt(false);
    markAsDismissed();
  }, [markAsDismissed]);

  // Reset for testing purposes
  const resetPromptState = useCallback(() => {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(NOTIFICATION_PROMPT_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  }, []);

  return {
    shouldShowPrompt,
    triggerContext,
    currentFeature,
    triggerPrompt,
    triggerPromptForFeature,
    closePrompt,
    dismissPrompt,
    dismissPromptGlobally,
    hasBeenShown,
    hasBeenDismissed,
    hasFeatureBeenShown,
    hasFeatureBeenDismissed,
    resetPromptState
  };
};
